/**
 * Report a problem — the return flow.
 *
 * The return windows are TIERED, and the app fetches them rather than assuming:
 *
 *   shortage / wrong product  48 hours — countable at the door, and the claim
 *                             must land while the route's van stock and
 *                             load-in variance are still open, or there is
 *                             nothing to reconcile it against
 *   damage / leakage /
 *   billing mismatch          7 days  — damage often only shows when a carton
 *                             is opened
 *
 * Choosing the category first, then showing its deadline, means a retailer is
 * never surprised by a rejection.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Chip, Empty, Money, PhotoTile, Skeleton, Stepper,
  TextArea, useToast,
} from '../../components/ui';
import { useQuery, useOnline } from '../../lib/useApi';
import { useT } from '../../lib/i18n';
import { api, ApiError, uploadMedia } from '../../lib/api';
import * as outbox from '../../lib/outbox';
import { capturePhoto, previewUrl, revokePreview, type CompressedImage } from '../../lib/camera';
import { dateShort, money } from '../../lib/format';

type Category = 'shortage' | 'damage' | 'wrong_product' | 'leakage' | 'billing_mismatch';

interface Windows {
  windowHours: Record<Category, number>;
  rationale: Record<string, string>;
}

interface InvoiceDetail {
  invoice: {
    id: string; invoice_number: string; invoice_date: string;
    net_amount_paise: number; customer_id: string;
  };
  items: {
    line_no: number; sku_code: string; product_name: string; uom: string;
    quantity: number; rate_paise: number; net_amount_paise: number;
    delivered_quantity: number | null;
  }[];
  immutable: boolean;
}

export default function RaiseReturn() {
  const t = useT();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();

  const invoice = useQuery<InvoiceDetail>(invoiceId ? `/api/invoices/${invoiceId}` : null);
  const windows = useQuery<Windows>('/api/returns/windows', {
    cacheKey: 'returns:windows', ttlSeconds: 86400,
  });

  const [category, setCategory] = useState<Category | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [photo, setPhoto] = useState<CompressedImage | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => () => { if (preview) revokePreview(preview); }, [preview]);

  // Item ids are not returned by the invoice endpoint's item rows, so lines are
  // keyed by line_no and mapped back on submit.
  const invoiceItemIds = useQuery<{ items: { id: string; line_no: number }[] }>(
    invoiceId ? `/api/invoices/${invoiceId}` : null,
  );

  const selectedLines = useMemo(
    () => Object.entries(quantities).filter(([, q]) => q > 0),
    [quantities],
  );

  const windowHours = category && windows.data ? windows.data.windowHours[category] : null;

  async function takePhoto() {
    setCapturing(true);
    const captured = await capturePhoto({ maxDimension: 1600, maxBytes: 2_500_000 });
    if (captured) {
      if (preview) revokePreview(preview);
      setPhoto(captured);
      setPreview(previewUrl(captured.blob));
    }
    setCapturing(false);
  }

  async function submit() {
    if (!category || selectedLines.length === 0 || !invoice.data) return;
    setBusy(true);

    try {
      // Photo evidence matters most for damage and leakage — it is what turns a
      // claim into something the supplier will accept.
      let photoMediaId: string | undefined;
      let localMediaId: string | undefined;

      if (photo) {
        if (online) {
          const uploaded = await uploadMedia({
            blob: photo.blob,
            purpose: 'return_photo',
            originalSizeBytes: photo.originalSizeBytes,
            widthPx: photo.width,
            heightPx: photo.height,
          });
          photoMediaId = uploaded.mediaId;
        } else {
          localMediaId = await outbox.stashMedia(photo.blob, 'return_photo');
        }
      }

      const body = {
        invoiceId: invoice.data.invoice.id,
        returnCategory: category,
        lines: selectedLines.map(([lineNo, qty]) => {
          const match = invoice.data!.items.find((i) => String(i.line_no) === lineNo);
          const idMatch = invoiceItemIds.data?.items.find((i) => i.line_no === Number(lineNo));
          return {
            invoiceItemId: idMatch?.id ?? '',
            claimedQuantity: qty,
            remarks: match?.product_name,
            ...(photoMediaId ? { photoMediaId } : {}),
          };
        }),
        remarks: remarks.trim() || undefined,
      };

      if (!online) {
        await outbox.enqueue({
          method: 'POST',
          path: '/api/returns',
          body,
          label: `${t('return.title')} — ${invoice.data.invoice.invoice_number}`,
          kind: 'return',
          mediaIds: localMediaId ? [localMediaId] : undefined,
        });
        toast.show(t('app.offlineHint'), 'ok');
        navigate('/orders');
        return;
      }

      const result = await api.post<{ returnNumber: string; message: string }>('/api/returns', body);
      toast.show(`${result.returnNumber} · ${t('return.submitted')}`, 'ok');
      navigate('/orders');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RETURN_WINDOW_CLOSED') {
        toast.show(err.message, 'danger');
      } else {
        toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
      }
    } finally {
      setBusy(false);
    }
  }

  if (invoice.loading && !invoice.data) {
    return <Page title={t('return.title')} back><Skeleton rows={4} /></Page>;
  }
  if (!invoice.data) {
    return <Page title={t('return.title')} back><Empty /></Page>;
  }

  const categories: { value: Category; label: string }[] = [
    { value: 'shortage', label: t('return.shortage') },
    { value: 'damage', label: t('return.damage') },
    { value: 'wrong_product', label: t('return.wrong_product') },
    { value: 'leakage', label: t('return.leakage') },
    { value: 'billing_mismatch', label: t('return.billing_mismatch') },
  ];

  return (
    <Page
      title={t('return.title')}
      subtitle={`${invoice.data.invoice.invoice_number} · ${dateShort(invoice.data.invoice.invoice_date)}`}
      back
    >
      <div className="stack">
        <Card>
          <div className="stack-2">
            <label className="t-sm t-semi">{t('return.category')}</label>
            <div className="stack-2">
              {categories.map((c) => (
                <button
                  key={c.value}
                  className="list__item"
                  style={{
                    border: `1px solid ${category === c.value ? 'var(--brand)' : 'var(--line)'}`,
                    borderRadius: 10,
                    background: category === c.value ? 'var(--line-2)' : 'transparent',
                  }}
                  onClick={() => setCategory(c.value)}
                  aria-pressed={category === c.value}
                >
                  <span className="grow t-sm t-semi">{c.label}</span>
                  {windows.data && (
                    <Chip tone={windows.data.windowHours[c.value] <= 48 ? 'warn' : 'neutral'}>
                      {windows.data.windowHours[c.value]}h
                    </Chip>
                  )}
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* The deadline and the reason for it. Explaining why a shortage window
            is short stops it reading as arbitrary. */}
        {category && windowHours && (
          <Banner tone={windowHours <= 48 ? 'warn' : 'info'}>
            <span className="t-sm">
              {t('return.windowHint', { hours: windowHours })}
              {windows.data?.rationale[category] && (
                <><br /><span className="t-xs">{windows.data.rationale[category]}</span></>
              )}
            </span>
          </Banner>
        )}

        {category && (
          <>
            <Card flush>
              <div className="card__header"><h3>{t('return.quantity')}</h3></div>
              <div className="list">
                {invoice.data.items.map((item) => {
                  const max = item.delivered_quantity ?? item.quantity;
                  return (
                    <div key={item.line_no} className="list__item list__item--static">
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="t-sm t-semi t-clip">{item.product_name}</div>
                        <div className="t-xs t-muted">
                          {max} {item.uom.toLowerCase()} · <Money paise={item.rate_paise} />
                        </div>
                      </div>
                      <Stepper
                        value={quantities[String(item.line_no)] ?? 0}
                        onChange={(n) => setQuantities((prev) => ({ ...prev, [String(item.line_no)]: n }))}
                        min={0}
                        max={max}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>

            <PhotoTile
              label={t('return.addPhoto')}
              hint={
                category === 'damage' || category === 'leakage'
                  ? 'A photo makes the claim much easier to approve'
                  : undefined
              }
              previewUrl={preview}
              busy={capturing}
              onCapture={() => void takePhoto()}
              onRemove={() => { if (preview) revokePreview(preview); setPhoto(null); setPreview(null); }}
            />

            <TextArea
              label={t('return.remarks')}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              maxLength={500}
            />

            {selectedLines.length > 0 && (
              <Card>
                <div className="row-between">
                  <span className="t-sm t-muted">{selectedLines.length} line(s) selected</span>
                  <span className="t-semi">
                    {selectedLines.reduce((s, [, q]) => s + q, 0)} {t('cart.quantity').toLowerCase()}
                  </span>
                </div>
              </Card>
            )}

            <Banner tone="info">
              <span className="t-xs">
                Approved returns are refunded, and a credit note is issued against
                the invoice.
              </span>
            </Banner>
          </>
        )}
      </div>

      {category && (
        <div className="actionbar">
          <Button
            block size="lg" busy={busy}
            disabled={selectedLines.length === 0}
            onClick={() => void submit()}
          >
            {t('app.submit')}
          </Button>
        </div>
      )}
    </Page>
  );
}
